import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Chip, CircularProgress,
  Stack, TextField, Typography,
} from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import * as api from "../../apiService/Core/countriesApi";
import type { AdminCountry, CountryPatch, StateOption } from "./types";
import DarkDialog from "../../Components/Push/DarkDialog";
import { formFieldSx, menuPaperSx } from "../../Components/Push/pushStyles";
import { InlineAlert } from "../../Components/Push/PushDisabledNotice";

interface Props {
  country: AdminCountry | null; // null = closed
  onClose: () => void;
  onSave: (alpha2: string, patch: CountryPatch) => Promise<unknown>;
}

export default function StatesDialog({ country, onClose, onSave }: Props) {
  const [states, setStates] = useState<StateOption[]>([]);
  const [disabled, setDisabled] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState("0");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!country) return;
    setDisabled(country.disabledStates);
    setSortOrder(String(country.sortOrder));
    setError(null);
    setStates([]);
    if (!country.hasStates) return;
    let alive = true;
    setLoading(true);
    api.listAllStates(country.alpha2)
      .then((r) => alive && setStates(r))
      .catch((e) => alive && setError(api.errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [country]);

  const selected = useMemo(() => states.filter((s) => disabled.includes(s.code)), [states, disabled]);
  const sortValue = Number(sortOrder);
  const sortValid = sortOrder.trim() !== "" && Number.isInteger(sortValue) && sortValue >= 0 && sortValue <= 100000;
  const allDisabled = !!country && states.length > 0 && disabled.length >= states.length;
  const blockedByRequired = !!country?.stateRequired && allDisabled;

  const submit = async () => {
    if (!country) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(country.alpha2, { disabledStates: disabled, sortOrder: sortValue });
      onClose();
    } catch (e) {
      setError(api.errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DarkDialog
      open={!!country}
      onClose={onClose}
      title={country ? `${country.name}: states and order` : ""}
      icon={<PublicOutlinedIcon sx={{ fontSize: 18 }} />}
      busy={saving}
      confirmText="Save"
      onConfirm={submit}
      confirmDisabled={loading || !sortValid || blockedByRequired}
    >
      <Stack spacing={2}>
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
        <TextField
          label="Sort order (lower shows first)"
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
          error={!sortValid}
          helperText={sortValid ? " " : "Whole number from 0 to 100000"}
          inputProps={{ inputMode: "numeric" }}
          sx={formFieldSx}
        />
        {country?.hasStates ? (
          <>
            <Typography variant="body2" sx={{ color: "#8a8a8a", fontSize: 12 }}>
              Selected states are hidden from signup and onboarding pickers. Existing users keep their saved state.
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={loading}
              options={states}
              value={selected}
              onChange={(_, value) => setDisabled(value.map((state) => state.code))}
              getOptionLabel={(state) => `${state.name} (${state.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              slotProps={{ paper: { sx: menuPaperSx } }}
              renderTags={(value, getTagProps) =>
                value.map((state, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={state.code}
                    size="small"
                    label={state.code}
                    title={state.name}
                    sx={{ color: "#c0c0c0", background: "#333" }}
                  />
                ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Disabled states"
                  sx={formFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>{loading ? <CircularProgress size={18} sx={{ color: "#ff6a3d" }} /> : null}{params.InputProps.endAdornment}</>),
                  }}
                />
              )}
            />
            {blockedByRequired && (
              <InlineAlert severity="warning">
                A state is required at signup for this country, so at least one state must stay enabled.
              </InlineAlert>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: "#8a8a8a", fontSize: 12 }}>This country has no states.</Typography>
        )}
      </Stack>
    </DarkDialog>
  );
}
